import { isUsableUrlTitle, urlDisplayTitle, type UrlEntity } from '../domain/note'
import type { LinkGistUi } from '../links/gistController'
import { buildLinkMenu, linkHost } from '../links/linkActions'
import type { LinkDisplayChoice } from '../links/linkDisplay'
import { hasExhaustedLinkMetadataRetries } from '../links/linkMetadata'
import { linkPreviewPresentation } from '../links/linkPreviewCategory'
import { LinkActionsMenu, copyLinkAddress } from './LinkActionsMenu'

/** What a pill or a row in the editor can do with its link beyond what the editor itself knows: the note's screen provides these. */
export interface EditorLinkActions {
  /** Present when this build shows summaries. */
  gist?: LinkGistUi
  /** Show the large preview (the link's summary, read at once). */
  onPreview?: () => void
  /** Open the full-screen view on the summary, an answer or the question field (`ask`). */
  onExpand?: (selected: string) => void
  /** Remove the link, keep its words. */
  onRemove?: () => void
}

interface LinkUnitMenuProps {
  /** The link's label in the note. */
  anchor: HTMLAnchorElement
  /** What is known about the page; null when no details exist for this link. */
  entity: UrlEntity | null
  extras?: EditorLinkActions
  onEdit: (anchor: HTMLAnchorElement) => void
  onRetryMetadata?: (url: UrlEntity) => void
  onDisplay?: (url: UrlEntity, choice: LinkDisplayChoice) => void
  /** Remove the link and its words; found from the address alone, so it is offered even before the page's details arrive. */
  onRemoveLink?: (anchor: HTMLAnchorElement) => void
  onClose: () => void
}

/**
 * What a tap on a pill or a row's ⋯ opens: the one link menu (links/linkActions.ts). A tap never edits the link: opening is its
 * own action, and changing the label or address is Edit. A pill's menu opens beside it, a row's beside its ⋯.
 */
export function LinkUnitMenu({ anchor, entity, extras, onEdit, onRetryMetadata, onDisplay, onRemoveLink, onClose }: LinkUnitMenuProps) {
  const href = anchor.getAttribute('href') ?? ''
  const label = (anchor.textContent ?? '').replace(/\u200B/g, '').trim()
  const presentation = entity ? linkPreviewPresentation(entity) : null
  const title = entity && isUsableUrlTitle(entity, entity.title) ? urlDisplayTitle(entity) : label || href
  const gist = extras?.gist
  const unavailable = Boolean(gist?.blockedReason)
  const model = buildLinkMenu({
    open: { href, label: presentation?.actionLabel ?? 'Open', host: linkHost(href) },
    onCopy: () => copyLinkAddress(href),
    onRetry: entity && onRetryMetadata && hasExhaustedLinkMetadataRetries(entity) ? () => onRetryMetadata(entity) : undefined,
    // A pill and a row have no summary line of their own, so Summary opens the preview, which reads the page and shows it.
    summary: gist && extras?.onPreview ? { state: entity?.gist ? (entity.gist.hidden ? 'hidden' : 'shown') : 'none', inPlace: false, unavailable, run: extras.onPreview } : undefined,
    ask: gist && extras?.onExpand ? { unavailable, run: () => extras.onExpand?.('ask') } : undefined,
    onEdit: () => onEdit(anchor),
    onRemove: extras?.onRemove,
    onRemoveLink: onRemoveLink ? () => onRemoveLink(anchor) : undefined,
    display: entity && onDisplay ? { value: anchor.getAttribute('data-note-link-display') === 'row' ? 'row' : 'chip', onChange: (choice) => onDisplay(entity, choice) } : undefined,
  })
  const unit = anchor.closest('[data-note-link-unit]')
  const trigger = unit?.querySelector<HTMLElement>('[data-note-link-action="more"]') ?? anchor
  return <LinkActionsMenu model={model} title={title} meta={presentation ? [presentation.label, presentation.provider].filter(Boolean).join(' · ') : linkHost(href)} anchor={trigger} onClose={onClose} />
}
